import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Wrench, Truck, Globe2, MessageCircle, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";
import api, { errMsg, fmtUSD, fmtDate, statusLabel, STATUS_COLORS } from "@/lib/api";
import StatusTracker from "@/components/StatusTracker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SERVICE_TYPES = [
  { key: "repair", label: "Estimado de reparación" },
  { key: "shipping", label: "Envío / transporte" },
  { key: "export", label: "Consultoría de exportación" },
];

export default function BidDetail() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const [bid, setBid] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [payMethod, setPayMethod] = useState("wire");
  const [serviceType, setServiceType] = useState("repair");
  const [serviceMsg, setServiceMsg] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/bid-requests/${id}`).then((r) => setBid(r.data)).catch((e) => setError(errMsg(e)));
  };
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <div className="max-w-4xl mx-auto px-4 py-24 text-center text-red-400" data-testid="bid-error">{error}</div>;
  if (!bid) return <div className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-400">{t("Cargando...")}</div>;

  const lot = bid.lot || {};
  const vehicle = `${lot.year || ""} ${lot.make || ""} ${lot.model || ""}`.trim() || `Lote ${lot.lot_number || ""}`;
  const wa = settings?.whatsapp_number || "13055550123";

  const payDeposit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/deposit/checkout", { bid_request_id: bid.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/bid-requests/${bid.id}/balance-marked`, { method: payMethod });
      toast.success(t("Pago reportado. Nuestro equipo lo confirmará."));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const sendServiceRequest = async () => {
    setBusy(true);
    try {
      await api.post("/service-requests", { type: serviceType, message: serviceMsg, bid_request_id: bid.id });
      toast.success(t("Solicitud de servicio registrada. Te contactaremos pronto."));
      setDialogOpen(false);
      setServiceMsg("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12" data-testid="bid-detail-page">
      <Link to="/panel" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5 mb-8">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("Volver al panel")}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="bid-vehicle">{vehicle}</h1>
          <p className="text-sm text-slate-400 mt-1">
            Lote #{lot.lot_number || "—"} {lot.url && (
              <a href={lot.url} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:text-blue-400 inline-flex items-center gap-1 ml-2">
                {t("Ver en Copart")} <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              </a>
            )}
          </p>
        </div>
        <Badge className={`${STATUS_COLORS[bid.status]} border text-sm px-4 py-1.5`} data-testid="bid-status-badge">
          {statusLabel(bid.status, lang)}
        </Badge>
      </div>

      <div className="card-tech rounded-xl p-8 mb-6">
        <StatusTracker status={bid.status} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Datos del lote + montos */}
        <div className="card-tech rounded-xl p-8">
          <h3 className="text-lg font-semibold mb-5">{t("Detalles")}</h3>
          {lot.image_url && <img src={lot.image_url} alt={vehicle} className="w-full h-44 object-cover rounded-lg border border-white/10 mb-5" />}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              [t("Millaje"), lot.mileage], [t("Título"), lot.title_type], [t("Ubicación"), lot.location],
              [t("Fecha de subasta"), lot.sale_date || t("Por confirmar")],
              [t("Puja máxima autorizada"), fmtUSD(bid.max_bid)], [t("Comisión de bróker"), fmtUSD(bid.flat_fee)],
              [t("Depósito"), fmtUSD(bid.deposit_amount)],
              ...(bid.final_price ? [[t("Precio final"), fmtUSD(bid.final_price)]] : []),
              ...(bid.balance_due != null ? [[t("Saldo por pagar"), fmtUSD(bid.balance_due)]] : []),
            ].map(([k, v], i) => (
              <div key={i} className="flex flex-col border-b border-white/5 pb-2">
                <dt className="text-xs text-slate-500">{k}</dt>
                <dd className="font-medium text-slate-200">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Acciones según estado */}
        <div className="space-y-6">
          {bid.status === "submitted" && (
            <div className="card-tech tracing-beam rounded-xl p-8" data-testid="action-pay-deposit">
              <h3 className="text-lg font-semibold mb-2">{t("Paga tu depósito para activar la puja")}</h3>
              <p className="text-sm text-slate-400 mb-6">
                {t("Nuestro equipo no puede pujar hasta que el depósito de {amount} esté confirmado. Si la fecha de subasta pasa antes de que se acredite, la solicitud se cancela y te avisamos.", { amount: fmtUSD(bid.deposit_amount) })}
              </p>
              <Button data-testid="pay-deposit-detail-btn" onClick={payDeposit} disabled={busy}
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full px-8 w-full sm:w-auto">
                <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {busy ? t("Redirigiendo...") : t("Pagar {amount} con tarjeta", { amount: fmtUSD(bid.deposit_amount) })}
              </Button>
            </div>
          )}

          {["won", "payment_due"].includes(bid.status) && (
            <div className="card-tech rounded-xl p-8 border-emerald-500/30" data-testid="action-pay-balance">
              <h3 className="text-lg font-semibold mb-2 text-emerald-300">{t("¡Ganamos tu subasta!")}</h3>
              <p className="text-sm text-slate-400 mb-4">
                {t("Precio final: {price}. Saldo por pagar: {balance} (precio + comisión − depósito).", { price: fmtUSD(bid.final_price), balance: fmtUSD(bid.balance_due) })}
              </p>
              <div className="text-xs bg-[#0B0F1A] border border-white/10 rounded-lg p-4 mb-5 space-y-2">
                <p className="text-slate-300 font-semibold">{t("Instrucciones de pago:")}</p>
                <p className="text-slate-400"><span className="text-slate-200">Zelle:</span> {settings?.zelle_email}</p>
                <p className="text-slate-400 whitespace-pre-line"><span className="text-slate-200">Wire:</span> {settings?.wire_instructions}</p>
              </div>
              {bid.balance_payment?.confirmed_at ? (
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border">{t("Pago confirmado")}</Badge>
              ) : bid.balance_payment?.marked_at ? (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 border" data-testid="balance-pending-badge">{t("Pago reportado — pendiente de confirmación")}</Badge>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger data-testid="pay-method-select" className="w-40 bg-[#0B0F1A] border-white/15"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wire">{t("Wire / transferencia")}</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid="mark-paid-btn" onClick={markPaid} disabled={busy}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">
                    {t("Ya envié el pago")}
                  </Button>
                </div>
              )}
            </div>
          )}

          {["lost", "refunded", "refund_pending"].includes(bid.status) && (
            <div className="card-tech rounded-xl p-8 border-red-500/20" data-testid="action-lost">
              <h3 className="text-lg font-semibold mb-2 text-red-300">{t("Subasta perdida")}</h3>
              {bid.refund ? (
                <p className="text-sm text-slate-400">
                  {t("Tu reembolso de {amount} fue procesado (cuota de procesamiento {fee}% = {feeAmount}). Puede tardar 5–10 días hábiles en reflejarse.", { amount: fmtUSD(bid.refund.refunded_amount), fee: bid.refund.fee_pct, feeAmount: fmtUSD(bid.refund.fee_amount) })}
                </p>
              ) : (
                <p className="text-sm text-slate-400">
                  {t("No ganamos esta vez. Tu depósito será reembolsado automáticamente a tu tarjeta menos la cuota de procesamiento divulgada.")}
                </p>
              )}
            </div>
          )}

          {/* Servicios */}
          <div className="card-tech rounded-xl p-8">
            <h3 className="text-lg font-semibold mb-2">{t("Servicios adicionales")}</h3>
            <p className="text-sm text-slate-400 mb-5">{t("Reparación, transporte y exportación para este vehículo.")}</p>
            <div className="flex flex-wrap gap-3">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="service-request-btn" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 hover:text-white rounded-full">
                    <Wrench className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("Solicitar servicio")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#111827] border-white/10 text-slate-50">
                  <DialogHeader><DialogTitle>{t("Solicitar servicio")}</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-2">
                    <Select value={serviceType} onValueChange={setServiceType}>
                      <SelectTrigger data-testid="service-type-select" className="bg-[#0B0F1A] border-white/15"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((s) => <SelectItem key={s.key} value={s.key}>{t(s.label)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Textarea data-testid="service-message" value={serviceMsg} onChange={(e) => setServiceMsg(e.target.value)}
                      placeholder={t("Cuéntanos qué necesitas (ej. cotizar reparación de transmisión)")}
                      className="bg-[#0B0F1A] border-white/15 min-h-[100px]" />
                    <Button data-testid="service-submit-btn" onClick={sendServiceRequest} disabled={busy}
                      className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">
                      {busy ? t("Enviando...") : t("Enviar solicitud")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`${t("Hola CarBid, consulta sobre mi solicitud")} ${bid.id.slice(0, 8)} — ${vehicle}`)}`}
                target="_blank" rel="noreferrer">
                <Button data-testid="whatsapp-btn" variant="outline"
                  className="border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 hover:text-emerald-200 rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("WhatsApp directo")}
                </Button>
              </a>
            </div>
          </div>

          {/* Historial */}
          <div className="card-tech rounded-xl p-8">
            <h3 className="text-lg font-semibold mb-5">{t("Historial")}</h3>
            <div className="space-y-4" data-testid="status-history">
              {[...(bid.status_history || [])].reverse().map((h, i) => (
                <div key={i} className="flex gap-3 text-sm fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <div className="w-2 h-2 rounded-full bg-[#2563EB] mt-1.5 shrink-0" />
                  <div>
                    <p className="text-slate-200 font-medium">{statusLabel(h.status, lang)}</p>
                    <p className="text-xs text-slate-500">{fmtDate(h.at)}{h.note ? ` — ${h.note}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
